import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { Box, Button, Modal, Typography } from '@mui/material';

const FLAG_KEY = 'sharpstack_ev_tutorial_v1';
const MOBILE_BREAKPOINT = 900;


// ───────────────────────────────────────────────────────────────────────────
// STEPS
// ───────────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    target: '[data-tutorial="sidebar"]',
    title: 'Navigation',
    content:
      "All your tools live here in the sidebar. Click any item to switch tools. You're on the EV Tool now — it's where you'll spend most of your time.",
    placement: 'right',
    skipBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '.filter-section',
    title: 'Filter your bets',
    content:
      'Filter by League, Category, Bet Type, and Book. Most important: under "Book", select only the sportsbooks you actually have accounts at — otherwise you\'ll see bets you can\'t place.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '[data-tutorial="settings-gear"]',
    title: 'Set your bankroll',
    content:
      'Click the gear to set your bankroll and Kelly fraction — the Kelly $ column shows recommended bet sizes once your bankroll is set. There are many other settings in the popup worth exploring too.',
    placement: 'bottom-end',
    skipBeacon: true,
  },
  {
    target: '.data-grid-container',
    title: 'Reading the table',
    content:
      'Each row is a +EV bet. Bets are sorted by Growth% by default — a bankroll-aware version of EV that prefers steady gains over high-variance longshots. Sharp Age and Target Age show how fresh the sharp consensus and target book lines are.',
    placement: 'center',
    skipBeacon: true,
  },
];

// ───────────────────────────────────────────────────────────────────────────
// CUSTOM TOOLTIP — full control over appearance + Step X of Y badge
// ───────────────────────────────────────────────────────────────────────────

const CustomTooltip = ({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => (
  <div
    {...tooltipProps}
    style={{
      backgroundColor: '#151a28',
      border: '1px solid #262d40',
      borderRadius: 12,
      padding: '20px 22px 16px',
      width: 340,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      fontFamily: 'inherit',
    }}
  >
    {/* Step X of Y badge */}
    <div
      style={{
        display: 'inline-block',
        background: '#4caf50',
        color: '#000',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 100,
        marginBottom: 10,
        letterSpacing: 0.5,
      }}
    >
      STEP {index + 1} OF 6
    </div>

    {/* Title */}
    <h3
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: '#fff',
        margin: '0 0 6px 0',
      }}
    >
      {step.title}
    </h3>

    {/* Body */}
    <p
      style={{
        fontSize: 13,
        color: '#8892a8',
        lineHeight: 1.55,
        margin: '0 0 16px 0',
      }}
    >
      {step.content}
    </p>

    {/* Actions */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <button
        {...skipProps}
        style={{
          background: 'none',
          border: 'none',
          color: '#666',
          fontSize: 11,
          cursor: 'pointer',
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        Skip tour
      </button>

      <div style={{ display: 'flex', gap: 6 }}>
        {index > 0 && (
          <button
            {...backProps}
            style={{
              background: 'none',
              border: '1px solid #262d40',
              color: '#8892a8',
              padding: '7px 12px',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back
          </button>
        )}
        <button
          {...primaryProps}
          style={{
            background: '#4caf50',
            color: '#000',
            border: 'none',
            padding: '7px 16px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {isLastStep ? 'Next →' : 'Next →'}
        </button>
      </div>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// JOYRIDE GLOBAL STYLES — only spotlight + overlay matter (tooltip is custom)
// ───────────────────────────────────────────────────────────────────────────

const joyrideStyles = {
  options: {
    zIndex: 10000,
    overlayColor: 'rgba(0, 0, 0, 0.72)',
    arrowColor: '#151a28',
  },
};

// ───────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ───────────────────────────────────────────────────────────────────────────

export default function EVTutorial({ isAuthenticated }) {
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);
  // 0 = none, 1 = Bet (step 5), 2 = Track (step 6)
  const [customStep, setCustomStep] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (localStorage.getItem(FLAG_KEY)) return;

    // Wait for the page to render before opening welcome
    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  // Listen for replay events from the sidebar
  useEffect(() => {
    const handleReplay = (e) => {
      if (e.detail?.flag !== FLAG_KEY) return; // not for this tutorial
      setCustomStep(0);
      setRunTour(false);
      setShowWelcome(true);
    };
    window.addEventListener('sharpstack:replay-tutorial', handleReplay);
    return () => window.removeEventListener('sharpstack:replay-tutorial', handleReplay);
  }, []);

  const startTour = () => {
    setShowWelcome(false);
    // Small delay so the modal unmounts before Joyride looks for targets
    setTimeout(() => setRunTour(true), 200);
  };

  const skipTour = () => {
    setShowWelcome(false);
    localStorage.setItem(FLAG_KEY, 'true');
  };

  const finishTutorial = () => {
    setCustomStep(0);
    localStorage.setItem(FLAG_KEY, 'true');
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED) {
      // Joyride finished → start custom steps with Bet
      setRunTour(false);
      setCustomStep(1);
    } else if (status === STATUS.SKIPPED) {
      localStorage.setItem(FLAG_KEY, 'true');
      setRunTour(false);
    }
  };

  return (
    <>
      {/* WELCOME MODAL */}
      <Modal open={showWelcome} onClose={() => {}} disableEscapeKeyDown>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#141820',
            border: '1px solid #252a38',
            borderRadius: '16px',
            p: '44px 40px 36px',
            width: 440,
            maxWidth: '90vw',
            textAlign: 'center',
            outline: 'none',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          }}
        >
          {/* Logo badge */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
              margin: '0 auto 22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              color: '#000',
              fontWeight: 800,
            }}
          >
            S
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>
            Welcome to SharpStack!
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#94a3b8',
              lineHeight: 1.6,
              mb: 4,
              px: 1,
            }}
          >
            Let's walk you through the EV Tool to get you started — a quick tour of the most important features.
          </Typography>

          <Button
            onClick={startTour}
            sx={{
              backgroundColor: '#4caf50',
              color: '#000',
              fontWeight: 700,
              px: 4,
              py: 1.25,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { backgroundColor: '#66bb6a' },
            }}
          >
            Show Me Around
          </Button>

          <Box sx={{ mt: 1.5 }}>
            <Button
              onClick={skipTour}
              sx={{
                color: '#4a5068',
                fontSize: 13,
                textTransform: 'none',
                '&:hover': { color: '#94a3b8', background: 'none' },
              }}
            >
              I'll explore on my own
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* JOYRIDE TOUR */}
      <Joyride
        steps={STEPS}
        run={runTour}
        onEvent={handleJoyrideCallback}
        continuous
        disableOverlayClose
        tooltipComponent={CustomTooltip}
        styles={joyrideStyles}
      />

      {/* CUSTOM STEP 5 — fixed position popup for "Place the bet" */}
      {customStep === 1 && (
        <ColumnStep
          fieldName="deeplink"
          stepNumber={5}
          title="Place the bet"
          content="Click any BET button to deep-link directly into your sportsbook's bet slip with the line pre-filled."
          nextLabel="Next →"
          onNext={() => setCustomStep(2)}
        />
      )}
      {customStep === 2 && (
        <ColumnStep
          fieldName="trackBet"
          stepNumber={6}
          title="Track your bets"
          content="After placing a bet, check the Track box to log it to your Bet Tracker. This builds your personal bet history so you can monitor your CLV and performance over time."
          nextLabel="Got it ✓"
          onNext={finishTutorial}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────────────
// COLUMN STEP — custom step with measured cutout on a specific column.
// Used for both step 5 (BET column) and step 6 (Track column).
// ───────────────────────────────────────────────────────────────────────────

function ColumnStep({ fieldName, stepNumber, title, content, nextLabel, onNext }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const measure = () => {
      const h = document.querySelector(`.MuiDataGrid-columnHeader[data-field="${fieldName}"]`)?.getBoundingClientRect();
      const g = document.querySelector('.data-grid-container')?.getBoundingClientRect();
      if (h && g) setRect({ left: h.left, top: h.top, width: h.width, height: g.bottom - h.top });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fieldName]);

  const clip = rect ? `polygon(
    0 0, 100vw 0, 100vw 100vh, 0 100vh, 0 ${rect.top}px,
    ${rect.left}px ${rect.top}px,
    ${rect.left}px ${rect.top + rect.height}px,
    ${rect.left + rect.width}px ${rect.top + rect.height}px,
    ${rect.left + rect.width}px ${rect.top}px,
    0 ${rect.top}px
  )` : 'none';

  // Position popup just to the left of the highlighted column
  const popupLeft = rect ? Math.max(20, rect.left - 360) : 24;
  const popupTop = rect ? Math.max(80, rect.top) : 180;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', clipPath: clip, zIndex: 9998 }} />
      <div style={{ position: 'fixed', top: popupTop, left: popupLeft, background: '#151a28', border: '1px solid #262d40', borderRadius: 12, padding: '20px 22px 16px', width: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', zIndex: 10000 }}>
        <div style={{ display: 'inline-block', background: '#4caf50', color: '#000', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 100, marginBottom: 10, letterSpacing: 0.5 }}>
          STEP {stepNumber} OF 6
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>
          {content}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onNext} style={{ background: '#4caf50', color: '#000', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {nextLabel}
          </button>
        </div>
      </div>
    </>
  );
}